import React, {
  useCallback, useEffect, useRef, useState,
} from "react"
import { isEmpty } from "lodash-es"
import { useModal } from "hooks"
import * as API from "services/api"
import { useHistoricalAccessToolContext } from "contexts/HistoricalAccessToolContext"
import { PrimaryButton, TertiaryButton } from "shared/buttons"
import Modal, { DEFAULT_MODAL_STYLE, ModalHeader } from "shared/Modal"
import { errorToast, successToast } from "shared/toast"
import ActivityIndicator from "shared/ActivityIndicator"
import HistoricalAccessToolPreviewList from "shared/HistoricalAccessTool/HistoricalAccessToolPreviewList"

const PREVIEW_DONE_STATUS = "done"
const PREVIEW_FAILED_STATUS = "failed"

const HistoricalAccessToolAddAccessPreview = () => {
  const { modalIsOpen, openModal, closeModal } = useModal()
  const [formSubmissionCounts, setFormSubmissionCounts] = useState([])
  const [previewStatus, setPreviewStatus] = useState(null)
  const pollingIntervalRef = useRef(null)

  const {
    allRequiredAddAccessInstructionsDataEntered,
    earliestSubmissionDate,
    latestSubmissionDate,
    reportableFieldLocationAnswerFilters,
    resetHistoricalAccessToolData,
    selectedAccessLevel,
    selectedFormsForSubmittingIds,
    selectedTaskFormIds,
    userId,
  } = useHistoricalAccessToolContext()

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  useEffect(() => () => stopPolling(), [stopPolling])

  const startPolling = useCallback((previewId) => {
    pollingIntervalRef.current = setInterval(async () => {
      const response = await API.getHistoricalAccessToolAddAccessPreview({ previewId })

      if (!response.ok) {
        stopPolling()
        setPreviewStatus(PREVIEW_FAILED_STATUS)
        errorToast("Something went wrong.  Unable to generate preview.")
        return
      }

      const { status, formSubmissionCounts: counts } = response.data
      setPreviewStatus(status)

      if (status === PREVIEW_DONE_STATUS) {
        stopPolling()
        setFormSubmissionCounts(counts || [])
      } else if (status === PREVIEW_FAILED_STATUS) {
        stopPolling()
        errorToast("Something went wrong.  Unable to generate preview.")
      }
    }, 2000)
  }, [stopPolling])

  const loadFormSubmissionCounts = async () => {
    const response = await API.historicalAccessToolAddAccessPreviews({
      accessLevel: selectedAccessLevel,
      earliestSubmissionDate,
      formsForSubmittingIds: selectedFormsForSubmittingIds,
      latestSubmissionDate,
      reportableFieldLocationAnswerFilters,
      taskFormIds: selectedTaskFormIds,
      userId,
    })

    if (response.ok) {
      const { id: previewId, status } = response.data
      setPreviewStatus(status)
      startPolling(previewId)
      return true
    }

    errorToast("Something went wrong. Unable to generate preview.")
    return false
  }

  const addAccess = async () => {
    const response = await API.historicalAccessToolAddAccess({
      accessLevel: selectedAccessLevel,
      earliestSubmissionDate,
      formsForSubmittingIds: selectedFormsForSubmittingIds,
      latestSubmissionDate,
      reportableFieldLocationAnswerFilters,
      taskFormIds: selectedTaskFormIds,
      userId,
    })

    if (response.ok) {
      successToast("Add access request received.  We'll email you with results once your request has been processed.")
      resetHistoricalAccessToolData()
      closeModal()
      setFormSubmissionCounts([])
      setPreviewStatus(null)
    } else {
      errorToast("Something went wrong.  Unable to add access.")
    }
  }

  const generatePreview = async () => {
    const success = await loadFormSubmissionCounts()
    if (success) openModal()
  }

  const cancel = () => {
    stopPolling()
    closeModal()
    setFormSubmissionCounts([])
    setPreviewStatus(null)
  }

  const previewIsLoading = previewStatus && previewStatus !== PREVIEW_DONE_STATUS && previewStatus !== PREVIEW_FAILED_STATUS

  return (
    <>
      <div className="flex justify-center">
        <PrimaryButton text="Preview" disabled={!allRequiredAddAccessInstructionsDataEntered} onClick={generatePreview} />
      </div>
      <Modal isOpen={modalIsOpen} style={DEFAULT_MODAL_STYLE}>
        <ModalHeader
          closeModal={cancel}
          heading="Add Access Preview"
        />
        {previewIsLoading && (
          <div className="flex justify-center mb-4">
            <ActivityIndicator />
          </div>
        )}
        {!previewIsLoading && !isEmpty(formSubmissionCounts) && (
          <HistoricalAccessToolPreviewList formSubmissionCounts={formSubmissionCounts} className="mb-4" />
        )}
        <p className="text-sm text-gray mb-8 text-center">
          If the user is not currently setup to gain access to future form submissions,
          there may be new submissions that arrive during or after this add-access request that the user will not have access to.
        </p>
        <div className="flex justify-center gap-4">
          <TertiaryButton text="Cancel" onClick={cancel} />
          <PrimaryButton text="Confirm" onClick={addAccess} disabled={previewStatus !== PREVIEW_DONE_STATUS} />
        </div>
      </Modal>
    </>
  )
}

export default HistoricalAccessToolAddAccessPreview
